import _defaultTo from 'lodash/defaultTo';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _filter from 'lodash/filter';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _last from 'lodash/last';
import _lowerCase from 'lodash/lowerCase';
import _isNil from 'lodash/isNil';
import _toUpper from 'lodash/toUpper';
import _find from 'lodash/find';
import _keys from 'lodash/keys';
import _some from 'lodash/some';
import _findLast from 'lodash/findLast';
// Serialization
import { deserialize, serialize } from 'serializr';
import { offerSchema, purchaseSchema } from './schemas';
// Permissions
import permissions from '../../constants/Permissions';
// Color
import { paymentStatusPalette } from '../../components/core/colors';
// Fragments
import { purchaseFragment } from './fragments';
// Custom
import { findInList, findLastInList } from '../../utils/Tools';

// Permissions used in model
const {
  pCpm: { parkingCategoryRead, customerRead },
} = permissions;

/**
 * Serialization
 */
const deserializeOffer = (data) => deserialize(offerSchema, data);
const deserializePurchase = (data) => deserialize(purchaseSchema, data);

const serializeOffer = (
  {
    customerId, parkingId, parkingCategoryId, begin, end, spacesQuantity, type,
  },
  hasPermission,
) => ({
  ...serialize(offerSchema, {
    ...(hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) && {
      customerId: _defaultTo(customerId, null),
    }),
    ...(!_isNil(parkingId) && { parkingId }),
    ...(hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }) && {
      parkingCategoryId: _defaultTo(parkingCategoryId, null),
    }),
    begin: _defaultTo(begin, null),
    end: _defaultTo(end, null),
    spacesQuantity: _defaultTo(spacesQuantity, null),
    ...(!_isNil(type) && { type: _toUpper(type) }),
  }),
});

/**
 * Lists
 */
const priceTypesList = [
  {
    key: 'free',
    value: 'free',
    labelId: 'parking.priceType.free',
    icon: 'parkingPriceTypeFree',
  },
  {
    key: 'paying',
    value: 'paying',
    labelId: 'parking.priceType.paying',
    icon: 'parkingPriceTypePaying',
  },
];
const paymentStatusList = [
  {
    key: 'cancelled',
    value: 'cancelled',
    labelId: 'paymentStatus.cancelled',
    icon: 'paymentStatusCancelled',
    color: paymentStatusPalette.cancelled,
  },
  {
    key: 'in_progress',
    value: 'in_progress',
    labelId: 'paymentStatus.inProgress',
    icon: 'paymentStatusInProgress',
    color: paymentStatusPalette.inProgress,
  },
  {
    key: 'refunded',
    value: 'refunded',
    labelId: 'paymentStatus.refunded',
    icon: 'paymentStatusRefunded',
    color: paymentStatusPalette.refunded,
  },
  {
    key: 'succeeded',
    value: 'succeeded',
    labelId: 'paymentStatus.succeeded',
    icon: 'paymentStatusSucceeded',
    color: paymentStatusPalette.succeeded,
  },
  {
    key: 'error',
    value: 'error',
    labelId: 'paymentStatus.error',
    icon: 'paymentStatusError',
    color: paymentStatusPalette.error,
  },
];
const purchasePaymentStatusList = [
  {
    key: 'cancelled',
    value: 'cancelled',
    labelId: 'paymentStatus.cancelled',
    icon: 'paymentStatusCancelled',
    color: paymentStatusPalette.cancelled,
    calloutSeverity: 'info',
  },
  {
    key: 'paid',
    value: 'paid',
    labelId: 'paymentStatus.paid',
    icon: 'paymentStatusPaid',
    color: paymentStatusPalette.paid,
    calloutSeverity: 'success',
  },
  {
    key: 'refunded',
    value: 'refunded',
    labelId: 'paymentStatus.refunded',
    icon: 'paymentStatusRefunded',
    color: paymentStatusPalette.refunded,
    calloutSeverity: 'info',
  },
  {
    key: 'required',
    value: 'required',
    labelId: 'paymentStatus.required',
    icon: 'paymentStatusRequired',
    color: paymentStatusPalette.required,
    calloutSeverity: 'warning',
  },
];
const paymentProviderList = [
  {
    key: 'external',
    value: 'external',
  },
  {
    key: 'free',
    value: 'free',
  },
  {
    key: 'stripe',
    value: 'stripe',
  },
];
const purchaseOfferTypesList = [
  {
    key: 'booking',
    value: 'booking',
  },
  {
    key: 'on_demand_definitive_exit',
    value: 'on_demand_definitive_exit',
  },
  {
    key: 'on_demand_temporary_exit',
    value: 'on_demand_temporary_exit',
  },
  {
    key: 'on_demand_overstay',
    value: 'on_demand_overstay',
  },
  {
    key: 'booking_overstay',
    value: 'booking_overstay',
  },
  {
    key: 'booking_extension',
    value: 'booking_extension',
  },
  {
    key: 'on_demand_extension',
    value: 'on_demand_extension',
  },
];

const purchasableProductsList = [
  {
    type: 'booking',
    queryParam: 'bookingReference',
    stateProductPath: 'cpmBooking.myBooking',
    paymentOptions: {
      operationSuccess: 'bookSuccess',
      operationSuccessLabelId: 'info.bookSuccess',
      successURI: 'my_bookings',
      withPaymentMethods: true,
      needsOnDemandCustomerInformation: false,
    },
  },
  {
    type: 'extendBooking',
    queryParam: 'extendBookingReference',
    stateProductPath: 'cpmBooking.myBooking',
    paymentOptions: {
      operationSuccess: 'bookExtensionSuccess',
      operationSuccessLabelId: 'info.bookExtensionSuccess',
      successURI: 'my_bookings',
      withPaymentMethods: true,
      needsOnDemandCustomerInformation: false,
    },
  },
  {
    type: 'overstayBooking',
    queryParam: 'overstayBookingReference',
    stateProductPath: 'cpmBooking.myBooking',
    paymentOptions: {
      operationSuccess: 'overstayPaidSuccess',
      operationSuccessLabelId: 'info.paid.success',
      operationSuccessFree: 'overstayFreeSuccess',
      operationSuccessFreeLabelId: 'info.overstayFreeSuccess',
      successURI: 'my_bookings',
      withPaymentMethods: true,
      needsOnDemandCustomerInformation: false,
    },
  },
  {
    type: 'onDemand',
    queryParam: 'onDemandReference',
    stateProductPath: 'cpmOnDemand.get',
    paymentOptions: {
      operationSuccess: 'selfCheckoutSuccess',
      operationSuccessLabelId: 'info.selfCheckoutSuccess',
      successURI: 'my_on_demand_access',
      withPaymentMethods: false,
      needsOnDemandCustomerInformation: true,
    },
  },
  {
    type: 'onDemand',
    queryParam: 'extendOnDemandReference',
    stateProductPath: 'cpmOnDemand.get',
    paymentOptions: {
      operationSuccess: 'extendMyOnDemandSuccess',
      operationSuccessLabelId: 'info.extendMyOnDemandSuccess',
      successURI: 'my_on_demand_access',
      withPaymentMethods: false,
      needsOnDemandCustomerInformation: false,
    },
  },
  {
    type: 'onDemand',
    queryParam: 'overstayOnDemandReference',
    stateProductPath: 'cpmOnDemand.get',
    paymentOptions: {
      operationSuccess: 'payMyOnDemandOverstaySuccess',
      operationSuccessLabelId: 'info.payMyOnDemandOverstaySuccess',
      successURI: 'my_on_demand_access',
      withPaymentMethods: false,
      needsOnDemandCustomerInformation: false,
    },
  },
];

const offerStatusList = [
  {
    key: 'expired',
    value: 'expired',
  },
  {
    key: 'progress',
    value: 'progress',
  },
  {
    key: 'used',
    value: 'used',
  },
];
/**
 * Getters
 */
const getPriceTypeFree = priceTypesList[0];
const paymentStatusSucceeded = paymentStatusList[3];
const paymentStatusError = paymentStatusList[4];

const getPurchasePaymentStatusCancelled = purchasePaymentStatusList[0];
const getPurchasePaymentStatusPaid = purchasePaymentStatusList[1];
const getPurchasePaymentStatusRefunded = purchasePaymentStatusList[2];
const getPurchasePaymentStatusRequired = purchasePaymentStatusList[3];

const getPaymentProviderFree = paymentProviderList[1];

const getPurchaseOfferTypeBooking = purchaseOfferTypesList[0];
const getPurchaseOfferTypeOndemandDefinitiveExit = purchaseOfferTypesList[1];
const getPurchaseOfferTypeOndemandTemporaryExit = purchaseOfferTypesList[2];
const getPurchaseOfferTypeOndemandOverstay = purchaseOfferTypesList[3];
const getPurchaseOfferTypeBookingOverstay = purchaseOfferTypesList[4];
const getPurchaseOfferTypeBookingExtend = purchaseOfferTypesList[5];
const getPurchaseOfferTypeOndemandExtend = purchaseOfferTypesList[6];

// purchasableProductsList
const getProductPurchasableBooking = purchasableProductsList[0];
const getProductPurchasableExtendBooking = purchasableProductsList[1];
const getProductPurchasableOverstayBooking = purchasableProductsList[2];
const getProductPurchasableOnDemand = purchasableProductsList[3];
// offer
const getOfferStatusExpired = offerStatusList[0];
const getOfferStatusProgress = offerStatusList[1];
const getOfferStatusUsed = offerStatusList[2];

/**
 * Utils
 */
const lastPurchase = (data) => ((data && data.purchases) ? _last(data.purchases) : null);
// Offer
const offerIsFree = (offerData) => _lowerCase(_get(offerData, 'priceType')) === getPriceTypeFree.value;
const offerAmountToPayBeforeServiceFeesIsZero = (offerData) => _get(offerData, 'discountedAmountInclTax') === 0;
const offerAmountToPayIsZero = (offerData) => _get(offerData, 'totalAmountInclTax') === 0;
const offerStatusIsProgress = (offerData) => _get(offerData, 'status') === getOfferStatusProgress.value;
const offerStatusIsUsed = (offerData) => _get(offerData, 'status') === getOfferStatusUsed.value;
const offerStatusIsExpired = (offerData) => _get(offerData, 'status') === getOfferStatusExpired.value;

const purchaseOfferType = (offerData) => findInList(purchaseOfferTypesList, 'value', offerData, 'type');
const purchaseOfferTypeIsBooking = (offerData) => _get(purchaseOfferType(offerData), 'value') === getPurchaseOfferTypeBooking.value;
const purchaseOfferTypeIsBookingExtension = (offerData) => _get(purchaseOfferType(offerData), 'value') === getPurchaseOfferTypeBookingExtend.value;
const purchaseOfferTypeIsBookingOverstay = (offerData) => _get(purchaseOfferType(offerData), 'value') === getPurchaseOfferTypeBookingOverstay.value;
const purchaseOfferTypeIsOndemandDefinitive = (offerData) => _get(purchaseOfferType(offerData), 'value') === getPurchaseOfferTypeOndemandDefinitiveExit.value;
const purchaseOfferTypeIsOndemandTemporary = (offerData) => _get(purchaseOfferType(offerData), 'value') === getPurchaseOfferTypeOndemandTemporaryExit.value;
const purchaseOfferTypeIsOndemandOverstay = (offerData) => _get(purchaseOfferType(offerData), 'value') === getPurchaseOfferTypeOndemandOverstay.value;
const purchaseOfferTypeIsOndemandExtend = (offerData) => _get(purchaseOfferType(offerData), 'value') === getPurchaseOfferTypeOndemandExtend.value;
const getPurchaseOfferType = (purchase) => purchaseOfferType(_get(purchase, 'offers[0]'));

const lastPurchasePriceType = (data) => findInList(priceTypesList, 'value', lastPurchase(data), 'type');
const lastPurchasePriceIsFree = (data) => _lowerCase(_get(lastPurchasePriceType(data), 'value')) === getPriceTypeFree.value;

// Payments
const paymentHasError = (payment) => _has(payment, 'stripeDetails.errors') && !_isEmpty(payment.stripeDetails.errors);
const paymentIsSucceededOrRefunded = (payment) => ['succeeded', 'refunded'].includes(_get(payment, 'status', ''));
const paymentGetErrors = (payment) => _get(payment, 'stripeDetails.errors', []);
const paymentStatus = (payment) => {
  if (paymentHasError(payment) && !paymentIsSucceededOrRefunded(payment)) {
    return paymentStatusError;
  }
  return findInList(paymentStatusList, 'value', payment, 'status');
};
const paymentIsSucceeded = (payment) => _get(paymentStatus(payment), 'value') === paymentStatusSucceeded.value;

// Purchase
const getPurchaseBooking = (purchases) => purchases.find((purchase) => purchase.offers.some(purchaseOfferTypeIsBooking));
const getBookingInitialPurchase = (purchases) => purchases.find((purchase) => purchase.offers.some((offer) => offer.type === 'booking'));
const getBookingLastOverstayPurchase = (purchases) => _findLast(purchases, ((purchase) => purchase.offers.some(purchaseOfferTypeIsBookingOverstay)));
const purchaseIsFree = (purchase) => _lowerCase(_get(purchase, 'type')) === getPriceTypeFree.value;
const purchaseIsZero = (purchase) => _get(purchase, 'totalAmountInclTax') === 0;
const purchaseHasNoPayment = (purchase) => _isEmpty(purchase.payments);
const lastPurchasePaymentStatus = (data) => findInList(purchasePaymentStatusList, 'value', lastPurchase(data), 'paymentStatus');
const lastPurchasePaymentIsPaid = (data) => _get(lastPurchasePaymentStatus(data), 'value') === getPurchasePaymentStatusPaid.value;
const lastPurchasePaymentIsRequired = (data) => _get(lastPurchasePaymentStatus(data), 'value') === getPurchasePaymentStatusRequired.value;
const lastPurchasePaymentCanBeValidated = (data) => {
  if (lastPurchasePriceIsFree(data)) {
    return false;
  }
  // Payment can be validated if status is required
  return lastPurchasePaymentIsRequired(data);
};

// Invoice

const getLastPurchasePaymentInvoice = (data) => {
  const purchase = lastPurchase(data);
  if (_isNil(purchase)) {
    return null;
  }

  return _get(purchase, 'paymentInvoice');
};

const purchasePaymentStatus = (purchase) => findLastInList(purchasePaymentStatusList, 'value', purchase, 'paymentStatus');
const purchasePaymentIsCancelled = (purchase) => _get(purchasePaymentStatus(purchase), 'value') === getPurchasePaymentStatusCancelled.value;
const purchasePaymentIsPaid = (purchase) => _get(purchasePaymentStatus(purchase), 'value') === getPurchasePaymentStatusPaid.value;
const purchasePaymentIsRefunded = (purchase) => _get(purchasePaymentStatus(purchase), 'value') === getPurchasePaymentStatusRefunded.value;
const purchasePaymentIsRequired = (purchase) => _get(purchasePaymentStatus(purchase), 'value') === getPurchasePaymentStatusRequired.value;
const purchasePaymentIsNotRequired = (purchase) => !purchasePaymentIsRequired(purchase);
const purchasePaymentCanBeValidated = (purchase, purchasePriceIsFree) => {
  if (purchasePriceIsFree) {
    return false;
  }
  // Payment can be validated if status is required
  return purchasePaymentIsRequired(purchase);
};

const purchaseSumPaidAmount = (purchases) => {
  const filteredPurchases = purchases.filter((purchase) => {
    const isPaid = /^paid$/i.test(purchase.paymentStatus);
    const isRefunded = /^refunded$/i.test(purchase.paymentStatus);
    return isPaid || isRefunded;
  });
  const sum = filteredPurchases.reduce((acc, purchase) => {
    let totalPaid = purchase.totalAmountInclTax;
    if (_get(purchase, 'refundedAmount', null) && typeof purchase.refundedAmount === 'number') totalPaid -= purchase.refundedAmount;
    return acc + totalPaid;
  }, 0);
  return sum;
};

const purchaseSumRefundedAmount = (purchase) => purchase.payments.reduce((totalRefunded, payment) => totalRefunded + payment.refundedAmount, 0);

const purchaseIsTotallyRefunded = (purchase) => purchasePaymentIsRefunded && purchase.totalAmountInclTax === purchase.refundedAmount;

const allPurchasesArePaid = (data) => {
  if (data && data.purchases) {
    return data.purchases.every((purchase) => purchase.paymentStatus.toLowerCase() === getPurchasePaymentStatusPaid.value);
  }

  return true;
};
const bookingPaymentProviderIsFree = (data) => _get(lastPurchase(data).payments[0], 'provider') === getPaymentProviderFree.value;

const onDemandSumAmountPaid = (onDemand) => {
  let sum = 0;
  const filteredData = _filter(onDemand.purchases, {
    paymentStatus: getPurchasePaymentStatusPaid.value,
  });
  _forEach(filteredData, (elem) => {
    sum += elem.totalAmountInclTax;
  });
  return sum;
};

const onDemandHasAlreadyPaidOverstay = (onDemand) => {
  const purchase = lastPurchase(onDemand);
  // One of the offer of the purchase has type on_demand_overstay
  const isOndemandOverstay = _find(purchase.offers, (offer) => purchaseOfferTypeIsOndemandOverstay(offer));
  // The last payment is in succeeded status
  const isSucceeded = paymentIsSucceeded(_last(purchase.payments));

  return Boolean(isOndemandOverstay && isSucceeded);
};

// Search in the purchasableProductsList if one of queryParam value match with the passed argument and retrive it
const findPurchasableProduct = (queryParams) => {
  const keys = _keys(queryParams);

  return _find(purchasableProductsList, (product) => _some(keys, (key) => product.queryParam === key));
};

// Check if the product is a booking and return a boolean
const productIsBooking = (product) => product.type === getProductPurchasableBooking.type;
// Check if the product is an extension booking and return a boolean
const productIsBookingExtension = (product) => product.type === getProductPurchasableExtendBooking.type;
// Check if the product is an overstay payment and return a boolean
const productIsBookingOverstay = (product) => product.type === getProductPurchasableOverstayBooking.type;
// Check if the product is a onDemand and return a boolean
const productIsOnDemand = (product) => product.type === getProductPurchasableOnDemand.type;
// Retrive the purchase by Id
const getPurchaseById = (purchases, id) => _find(purchases, { id: +id });

/**
 * GraphQL
 */
const mutInitiatePayment = `
  mutation InitiatePayment($input: InitiatePaymentInput!) {
    initiatePayment(input: $input) {
      ...purchaseFragment
    }
  }

  ${purchaseFragment}
`;

const mutRefundPurchase = `
  mutation RefundPurchase($input: RefundPurchaseInput!) {
    refundPurchase(input: $input) {
      id
    }
  }
`;

/**
 * Model
 */
const finance = {
  // Name
  name: 'finance',

  // Serialization
  deserializeOffer,
  deserializePurchase,
  serializeOffer,

  // Utils
  lastPurchase,
  offerIsFree,
  offerAmountToPayBeforeServiceFeesIsZero,
  offerAmountToPayIsZero,
  offerStatusIsProgress,
  offerStatusIsUsed,
  offerStatusIsExpired,

  getPurchaseBooking,

  purchaseOfferType,
  purchaseOfferTypeIsBooking,
  purchaseOfferTypeIsBookingExtension,
  purchaseOfferTypeIsBookingOverstay,
  purchaseOfferTypeIsOndemandOverstay,
  purchaseOfferTypeIsOndemandDefinitive,
  purchaseOfferTypeIsOndemandTemporary,
  purchaseOfferTypeIsOndemandExtend,
  getPurchaseOfferType,

  onDemandHasAlreadyPaidOverstay,

  lastPurchasePriceType,
  lastPurchasePriceIsFree,

  paymentStatus,
  paymentIsSucceededOrRefunded,
  paymentHasError,
  paymentGetErrors,
  paymentIsSucceeded,

  getBookingInitialPurchase,
  getBookingLastOverstayPurchase,
  purchaseIsFree,
  purchaseIsZero,
  purchaseHasNoPayment,
  lastPurchasePaymentCanBeValidated,
  lastPurchasePaymentStatus,
  lastPurchasePaymentIsPaid,
  lastPurchasePaymentIsRequired,
  purchasePaymentIsNotRequired,
  purchasePaymentStatus,
  purchasePaymentIsCancelled,
  purchasePaymentIsPaid,
  purchasePaymentIsRequired,
  purchasePaymentIsRefunded,
  purchasePaymentCanBeValidated,
  purchaseSumPaidAmount,
  purchaseSumRefundedAmount,
  purchaseIsTotallyRefunded,
  allPurchasesArePaid,

  getPriceTypeFree,
  getPurchasePaymentStatusPaid,
  getPurchasePaymentStatusRefunded,

  getLastPurchasePaymentInvoice,

  getPurchasePaymentStatusCancelled,
  getPurchasePaymentStatusRequired,

  bookingPaymentProviderIsFree,

  onDemandSumAmountPaid,

  getProductPurchasableBooking,
  getProductPurchasableOnDemand,
  getProductPurchasableOverstayBooking,
  findPurchasableProduct,
  productIsBooking,
  productIsBookingExtension,
  productIsBookingOverstay,
  productIsOnDemand,
  getPurchaseById,

  // GraphQL
  mutInitiatePayment,
  mutRefundPurchase,
};

module.exports = finance;



// WEBPACK FOOTER //
// ./models/cpm/finance.js